// ============================================================================
// LOCAL AUTH CONTEXT
// ============================================================================
// Lightweight context for the local-login flow (time_trck.tbl_user + bcrypt JWT).
// Independent of the TenantHub AuthContext — coexists with it.
// Persists the JWT via the existing tokenManager so the shared axiosClient
// interceptor automatically attaches the Bearer header.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axiosClient from '../api/client/axiosClient';
import { tokenManager } from '../auth/tokenManager';
import { STORAGE_KEYS } from '../auth/storageKeys';

export interface LocalUser {
  id: string;
  username: string;
  email: string;
  fullName: string;
  role: 'Admin' | 'User' | string;
}

interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface LoginResponseBody {
  token: string;
  expiresAt: string;
  expiresIn: number;
  username: string;
  tenantId: string;
  roles: string[];
}

interface RegisterResponseBody {
  token: string;
  expiresAt: string;
  expiresIn: number;
  user: LocalUser;
  tenantId: string;
}

interface LocalAuthContextValue {
  user: LocalUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (req: { username: string; email: string; fullName: string; password: string; role?: string }) => Promise<void>;
  logout: () => void;
}

const LocalAuthContext = createContext<LocalAuthContextValue | null>(null);

// ============================================================================
// main_layout integration:
// main_layout.tsx reads four keys from localStorage to render its sidebar/header:
//   - 'user'            → { name, role, avatar, permissions }
//   - 'APP_CONFIG'      → { appTitle }
//   - 'NAVIGATION_MENU' → NavigationSection[]
//   - 'MODULES_CONFIG'  → { [moduleId]: { componentName, title, ... } }
// Module IDs reference component names registered in COMPONENT_REGISTRY.
// ============================================================================
// Icon keys must match the inline IconSystem in src/Layout/main_layout.tsx.
// Supported keys include: arrowLeft, chartofAccount, chevronDown, chevronRight,
// collapse, configure, currency, file, folder, footer, home, lock, logout, menu,
// notification, plus, search, service, settings, tabs, user, users, window.
const TIME_TRACKER_NAV = [
  {
    section: 'Time Tracker',
    icon: 'service',
    items: [
      { id: 'tt-tasks',         title: 'Tasks',         icon: 'service' },
      { id: 'tt-my-timesheet',  title: 'My Timesheet',  icon: 'file' },
      { id: 'tt-clients',       title: 'Clients',       icon: 'users' },
      { id: 'tt-projects',      title: 'Projects',      icon: 'folder' },
      { id: 'tt-task-types',    title: 'Task Types',    icon: 'configure' },
      { id: 'tt-users',         title: 'Users',         icon: 'user' },
      { id: 'tt-reports',       title: 'Reports',       icon: 'chartofAccount' },
    ],
  },
];

const TIME_TRACKER_MODULES = {
  'tt-tasks': {
    showHeader: false,
    title: 'Tasks',
    description: 'Browse tasks, log time, close them out.',
    icon: 'service',
    componentName: 'TtTaskListScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: [],
    breadcrumb: ['Time Tracker', 'Tasks'],
  },
  'tt-my-timesheet': {
    showHeader: false,
    title: 'My Timesheet',
    description: 'Every entry you have logged — task-bound and ad-hoc.',
    icon: 'file',
    componentName: 'TtMyTimesheetScreen',
    actions: ['edit', 'delete'],
    permissions: [],
    breadcrumb: ['Time Tracker', 'My Timesheet'],
  },
  'tt-clients': {
    showHeader: false,
    title: 'Clients',
    description: 'Manage clients.',
    icon: 'users',
    componentName: 'TtClientMasterScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: [],
    breadcrumb: ['Time Tracker', 'Clients'],
  },
  'tt-projects': {
    showHeader: false,
    title: 'Projects',
    description: 'Standalone project list (decoupled from clients).',
    icon: 'folder',
    componentName: 'TtProjectMasterScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: [],
    breadcrumb: ['Time Tracker', 'Projects'],
  },
  'tt-task-types': {
    showHeader: false,
    title: 'Task Types',
    description: 'Manage task categories.',
    icon: 'configure',
    componentName: 'TtTaskTypeMasterScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: [],
    breadcrumb: ['Time Tracker', 'Task Types'],
  },
  'tt-users': {
    showHeader: false,
    title: 'Users',
    description: 'Manage who can log in. Admin-only.',
    icon: 'user',
    componentName: 'TtUserMasterScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: ['admin'],
    breadcrumb: ['Time Tracker', 'Users'],
  },
  'tt-reports': {
    showHeader: false,
    title: 'Reports',
    description: 'Time totals grouped by user, client or task.',
    icon: 'chartofAccount',
    componentName: 'TtReportsScreen',
    actions: [],
    permissions: [],
    breadcrumb: ['Time Tracker', 'Reports'],
  },
};

const seedMainLayoutConfig = (user: LocalUser) => {
  localStorage.setItem('user', JSON.stringify({
    name: user.fullName || user.username,
    role: user.role,
    avatar: (user.fullName || user.username).split(' ').map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase(),
    permissions: user.role === 'Admin' ? ['admin', 'read', 'write'] : ['read', 'write'],
  }));
  localStorage.setItem('APP_CONFIG', JSON.stringify({ appTitle: 'Time Tracker' }));
  localStorage.setItem('NAVIGATION_MENU', JSON.stringify(TIME_TRACKER_NAV));
  localStorage.setItem('MODULES_CONFIG', JSON.stringify(TIME_TRACKER_MODULES));
};

const persistSession = (token: string, tenantId: string, user: LocalUser) => {
  tokenManager.setAccessToken(token);
  tokenManager.setTenantId(tenantId);
  localStorage.setItem(STORAGE_KEYS.USER_NAME, user.username);
  localStorage.setItem(STORAGE_KEYS.USER_EMAIL, user.email);
  localStorage.setItem(STORAGE_KEYS.FULL_NAME, user.fullName);
  localStorage.setItem('local_user', JSON.stringify(user));
  seedMainLayoutConfig(user);
};

const readPersistedUser = (): LocalUser | null => {
  const raw = localStorage.getItem('local_user');
  if (!raw) return null;
  try { return JSON.parse(raw) as LocalUser; } catch { return null; }
};

export const LocalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<LocalUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Hydrate from localStorage on mount
  useEffect(() => {
    const token = tokenManager.getAccessToken();
    if (!token) { setIsLoading(false); return; }

    // Optimistic: use cached user if present.
    // Re-seed nav/module config on every hydrate so code changes to
    // TIME_TRACKER_NAV / TIME_TRACKER_MODULES take effect without requiring
    // a full logout — the stale localStorage entries would otherwise persist.
    const cached = readPersistedUser();
    if (cached) {
      setUser(cached);
      seedMainLayoutConfig(cached);
    }

    // Re-verify against /auth/me
    (async () => {
      try {
        const res = await axiosClient.get<ApiEnvelope<LocalUser>>('/v1/auth/me');
        const me = res.data?.data;
        if (me) {
          setUser(me);
          localStorage.setItem('local_user', JSON.stringify(me));
        }
      } catch {
        tokenManager.clearAll();
        localStorage.removeItem('local_user');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await axiosClient.post<LoginResponseBody>('/v1/auth/login', { username, password });
    const body = res.data;

    // /me round-trip to get the full user record (login response is sparser)
    persistSession(body.token, body.tenantId, {
      id: '', username: body.username, email: '', fullName: body.username, role: body.roles?.[0] ?? 'User',
    });
    const me = await axiosClient.get<ApiEnvelope<LocalUser>>('/v1/auth/me');
    const fullUser = me.data.data;
    persistSession(body.token, body.tenantId, fullUser);
    setUser(fullUser);
  }, []);

  const register = useCallback(async (req: { username: string; email: string; fullName: string; password: string; role?: string }) => {
    const res = await axiosClient.post<ApiEnvelope<RegisterResponseBody>>('/v1/auth/register', {
      username: req.username,
      email: req.email,
      fullName: req.fullName,
      password: req.password,
      role: req.role ?? 'User',
    });
    const body = res.data.data;
    persistSession(body.token, body.tenantId, body.user);
    setUser(body.user);
  }, []);

  const logout = useCallback(() => {
    tokenManager.clearAll();
    localStorage.removeItem('local_user');
    localStorage.removeItem('user');
    localStorage.removeItem('APP_CONFIG');
    localStorage.removeItem('NAVIGATION_MENU');
    localStorage.removeItem('MODULES_CONFIG');
    setUser(null);
  }, []);

  const value = useMemo<LocalAuthContextValue>(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
  }), [user, isLoading, login, register, logout]);

  return <LocalAuthContext.Provider value={value}>{children}</LocalAuthContext.Provider>;
};

export const useLocalAuth = (): LocalAuthContextValue => {
  const ctx = useContext(LocalAuthContext);
  if (!ctx) throw new Error('useLocalAuth must be used within LocalAuthProvider');
  return ctx;
};
